import React, { useState, useEffect, useCallback } from 'react';
import { Card, Button, Tag, Modal, InputNumber, Checkbox, Spin, Typography, message, Alert } from 'antd';
import { PlayCircleOutlined, ClockCircleOutlined, CheckCircleOutlined, CloseCircleOutlined, HistoryOutlined, WarningOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import api from '../../api/axios';

const { Text, Paragraph } = Typography;

const STATUS_TAG = {
  running:   <Tag color="processing" icon={<Spin size="small" style={{ marginRight: 4 }} />}>Çalışıyor</Tag>,
  completed: <Tag color="success" icon={<CheckCircleOutlined />}>Tamamlandı</Tag>,
  failed:    <Tag color="error" icon={<CloseCircleOutlined />}>Hata</Tag>,
  // "Atlandı": komut çalıştı ama iş yapmadan çıktı (ör. Google hesabı bağlı
  // değilken Drive yedeği). Yeşil göstermek yanlıştı — 03-10.09.2026 arası
  // yedi gece yedek Drive'a gitmedi ve panel her sabah "Tamamlandı" dedi.
  skipped:   <Tag color="warning" icon={<WarningOutlined />}>Atlandı</Tag>,
};

const CommandRunsTab = () => {
  const [commands, setCommands]       = useState([]);
  const [loading, setLoading]         = useState(true);
  const [running, setRunning]         = useState({}); // key -> bool
  const [params, setParams]           = useState({}); // key -> { param: value }
  const [resultModal, setResultModal] = useState(null); // { label, output, status, duration }
  const [historyModal, setHistoryModal] = useState(null); // { key, label, runs }

  const fetchCommands = useCallback(async () => {
    try {
      const res = await api.get('/commands');
      setCommands(res.data);
      // Varsayılan parametre değerlerini yükle
      const defaults = {};
      res.data.forEach(cmd => {
        defaults[cmd.key] = {};
        Object.entries(cmd.params || {}).forEach(([p, config]) => {
          defaults[cmd.key][p] = config.default ?? '';
        });
      });
      setParams(defaults);
    } catch {
      message.error('Komutlar yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCommands(); }, [fetchCommands]);

  // Komut artık arka planda (kuyrukta) çalışıyor — hemen "running" dönüyor,
  // sonucu Geçmiş listesinden kısa aralıklarla kontrol edip yakalıyoruz.
  const pollRunResult = (cmd, runId, attempt = 0) => {
    const MAX_ATTEMPTS = 150; // ~5 dakika (2sn aralıkla)
    setTimeout(async () => {
      try {
        const historyRes = await api.get(`/commands/${cmd.key}/history`);
        const run = historyRes.data.find(r => r.id === runId);

        if (run && run.status !== 'running') {
          setResultModal({
            label:    cmd.label,
            status:   run.status,
            output:   run.output,
            duration: run.duration_seconds,
          });
          setRunning(r => ({ ...r, [cmd.key]: false }));
          fetchCommands();
          return;
        }

        if (attempt >= MAX_ATTEMPTS) {
          message.warning('Komut hâlâ arka planda çalışıyor. Sonucu birazdan "Geçmiş"ten kontrol edebilirsiniz.');
          setRunning(r => ({ ...r, [cmd.key]: false }));
          fetchCommands();
          return;
        }

        pollRunResult(cmd, runId, attempt + 1);
      } catch {
        setRunning(r => ({ ...r, [cmd.key]: false }));
      }
    }, 2000);
  };

  const handleRun = async (cmd) => {
    setRunning(r => ({ ...r, [cmd.key]: true }));
    try {
      const res = await api.post(`/commands/${cmd.key}/run`, params[cmd.key] || {});
      message.info(res.data.message || 'Komut arka planda çalıştırılıyor...');
      pollRunResult(cmd, res.data.id);
    } catch (err) {
      setResultModal({
        label:  cmd.label,
        status: 'failed',
        output: err?.response?.data?.output || err?.response?.data?.message || 'Beklenmeyen hata.',
      });
      setRunning(r => ({ ...r, [cmd.key]: false }));
    }
  };

  const handleHistory = async (cmd) => {
    const res = await api.get(`/commands/${cmd.key}/history`);
    setHistoryModal({ key: cmd.key, label: cmd.label, runs: res.data });
  };

  if (loading) return <div className="flex justify-center py-12"><Spin /></div>;

  const attentionCommands = commands.filter(c => c.needs_attention);

  return (
    <div className="space-y-4">
      {attentionCommands.length > 0 && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined />}
          message={`${attentionCommands.length} zamanlanmış görev dikkat gerektiriyor`}
          description={
            <ul className="list-disc pl-5 m-0">
              {attentionCommands.map(c => (
                <li key={c.key}>
                  {c.label} — {c.is_stale ? 'beklenen sürede çalışmadı' : 'son çalışması hata verdi'}
                </li>
              ))}
            </ul>
          }
        />
      )}

      {commands.map(cmd => {
        const last = cmd.last_run;
        const isRunning = running[cmd.key];
        return (
          <Card key={cmd.key} size="small">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-base flex items-center gap-2">
                  {cmd.label}
                  {cmd.needs_attention && (
                    <Tag color="warning" icon={<WarningOutlined />}>
                      {cmd.is_stale ? 'Gecikmiş' : 'Kontrol Et'}
                    </Tag>
                  )}
                </div>
                <div className="text-gray-500 text-sm mt-0.5">{cmd.description}</div>
                {cmd.schedule && (
                  <div className="text-xs text-blue-500 mt-1 flex items-center gap-1">
                    <ClockCircleOutlined />
                    {cmd.schedule}
                  </div>
                )}

                {/* Parametreler */}
                {Object.entries(cmd.params || {}).length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-4">
                    {Object.entries(cmd.params).map(([pKey, pConfig]) => (
                      <div key={pKey} className="flex items-center gap-2">
                        {pConfig.type === 'boolean' ? (
                          <Checkbox
                            checked={params[cmd.key]?.[pKey] ?? pConfig.default}
                            onChange={e => setParams(p => ({
                              ...p,
                              [cmd.key]: { ...p[cmd.key], [pKey]: e.target.checked },
                            }))}
                          >
                            <Text className="text-sm text-gray-600">{pConfig.label}</Text>
                          </Checkbox>
                        ) : (
                          <>
                            <Text className="text-sm text-gray-600">{pConfig.label}:</Text>
                            <InputNumber
                              size="small"
                              min={pConfig.min}
                              max={pConfig.max}
                              value={params[cmd.key]?.[pKey] ?? pConfig.default}
                              onChange={val => setParams(p => ({
                                ...p,
                                [cmd.key]: { ...p[cmd.key], [pKey]: val },
                              }))}
                              style={{ width: 80 }}
                            />
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Son çalışma */}
                {last && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap text-sm text-gray-500">
                    <ClockCircleOutlined />
                    <span>Son: {dayjs(last.started_at).format('DD.MM.YYYY HH:mm')}</span>
                    {STATUS_TAG[last.status]}
                    {last.duration_seconds != null && (
                      <span>{last.duration_seconds}sn</span>
                    )}
                    <span>— {last.triggered_by || 'Otomatik (zamanlanmış)'}</span>
                  </div>
                )}
                {!last && (
                  <div className="mt-2 text-sm text-gray-400">Henüz çalıştırılmadı.</div>
                )}
              </div>

              <div className="flex gap-2 shrink-0">
                <Button
                  icon={<HistoryOutlined />}
                  size="small"
                  onClick={() => handleHistory(cmd)}
                >
                  Geçmiş
                </Button>
                {cmd.manual_run !== false && (
                  <Button
                    type="primary"
                    icon={<PlayCircleOutlined />}
                    loading={isRunning}
                    onClick={() => handleRun(cmd)}
                  >
                    Çalıştır
                  </Button>
                )}
              </div>
            </div>
          </Card>
        );
      })}

      {/* Sonuç Modal */}
      <Modal
        title={resultModal?.label}
        open={!!resultModal}
        onCancel={() => setResultModal(null)}
        footer={<Button onClick={() => setResultModal(null)}>Kapat</Button>}
        width={640}
      >
        {resultModal && (
          <div>
            <div className="mb-3">{STATUS_TAG[resultModal.status]}</div>
            {resultModal.duration != null && (
              <div className="text-sm text-gray-500 mb-3">{resultModal.duration} saniyede tamamlandı.</div>
            )}
            {resultModal.output && (
              <pre className="bg-gray-100 rounded p-3 text-xs overflow-auto max-h-80 whitespace-pre-wrap">
                {resultModal.output}
              </pre>
            )}
          </div>
        )}
      </Modal>

      {/* Geçmiş Modal */}
      <Modal
        title={`${historyModal?.label} — Son 10 Çalışma`}
        open={!!historyModal}
        onCancel={() => setHistoryModal(null)}
        footer={<Button onClick={() => setHistoryModal(null)}>Kapat</Button>}
        width={600}
      >
        {historyModal?.runs?.length === 0 && (
          <p className="text-gray-400 text-center py-4">Henüz çalıştırılmamış.</p>
        )}
        {historyModal?.runs?.map(run => (
          <div key={run.id} className="mb-3 border-b pb-3 last:border-0">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                {STATUS_TAG[run.status]}
                <span className="text-sm">{dayjs(run.started_at).format('DD.MM.YYYY HH:mm:ss')}</span>
                {run.duration_seconds != null && (
                  <span className="text-xs text-gray-400">{run.duration_seconds}sn</span>
                )}
              </div>
              <span className="text-xs text-gray-400">{run.triggered_by?.name || 'Otomatik (zamanlanmış)'}</span>
            </div>
            {run.output && (
              <pre className="bg-gray-50 rounded p-2 mt-2 text-xs overflow-auto max-h-32 whitespace-pre-wrap">
                {run.output}
              </pre>
            )}
          </div>
        ))}
      </Modal>
    </div>
  );
};

export default CommandRunsTab;
